// Render a contact's *current* local time from the IANA timezone the caller-queue
// derives from their phone number (e.g. "America/New_York" → "2:42pm ET").
// Returns '' when the zone is missing/invalid.

export function formatLocalTime(timezone: string | null | undefined, at: Date = new Date()): string {
  const tz = String(timezone || '').trim()
  if (!tz) return ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).formatToParts(at)
    const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
    const hour = get('hour')
    const minute = get('minute')
    const ap = get('dayPeriod').toLowerCase().startsWith('p') ? 'pm' : 'am'
    let tzName = get('timeZoneName') // "EDT" | "PST" | "GMT+5:30" | …
    // Collapse North-American daylight/standard pairs to the generic form the
    // mockups use: EDT/EST → ET, PDT/PST → PT, CDT/CST → CT, MDT/MST → MT.
    const m = /^([A-Z])[DS]T$/.exec(tzName)
    if (m) tzName = `${m[1]}T`
    if (!hour || !minute) return ''
    return `${hour}:${minute}${ap} ${tzName}`.trim()
  } catch {
    return ''
  }
}

// Compact "time since" for the last-connect line / signal chips ("6d ago").
export function relativeAgo(iso: string | null | undefined, at: Date = new Date()): string {
  const s = String(iso || '').trim()
  if (!s) return ''
  const then = new Date(s)
  if (Number.isNaN(then.getTime())) return ''
  const sec = Math.max(0, Math.round((at.getTime() - then.getTime()) / 1000))
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.round(hr / 24)
  if (d < 7) return `${d}d ago`
  if (d < 30) return `${Math.round(d / 7)}w ago`
  if (d < 365) return `${Math.round(d / 30)}mo ago`
  return `${Math.round(d / 365)}y ago`
}

// Returns "——" on null/invalid (the empty-cell glyph used across the history table).
export function formatAttemptStamp(iso: string | null | undefined): string {
  const s = String(iso || '').trim()
  if (!s) return '——'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return '——'
  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
  const ap = get('dayPeriod').toLowerCase().startsWith('p') ? 'pm' : 'am'
  return `${get('month')} ${get('day')} ${get('year')} · ${get('hour')}:${get('minute')}${ap}`
}

function _mmss(totalSec: number): string {
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

// Call duration "0:38" (m:ss). Empty string for null / non-positive (e.g. a touch that
// never connected) so the UI renders nothing rather than "0:00".
export function formatDuration(seconds: number | null | undefined): string {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n <= 0) return ''
  return _mmss(Math.floor(n))
}

// Transcript-utterance timestamp "0:02" (m:ss); "0:00" at the start of the call.
export function formatClock(seconds: number | null | undefined): string {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n < 0) return '0:00'
  return _mmss(Math.floor(n))
}
